import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ConfigCatFeatureFlag } from "util/CommonConstant";
import { DATASOURCE_BE_KEYS } from "util/DataSourceConstant";

export interface ConfigCatFlagState {
  [ConfigCatFeatureFlag.CHANGE_API_URL]: boolean;
  [ConfigCatFeatureFlag.AUTO_GENERATE_AWS_EXTERNAL_ID]: boolean;
  [ConfigCatFeatureFlag.ENABLED_CONNECTIONS]: DATASOURCE_BE_KEYS[];
  [ConfigCatFeatureFlag.ENABLE_AWS_OIDC_FORM]: boolean;
  [ConfigCatFeatureFlag.ENABLE_AWS_ACCESS_KEY_FORM]: boolean;
  [ConfigCatFeatureFlag.ENABLE_SESSION_FILE_UPLOAD]: boolean;
  [ConfigCatFeatureFlag.ENABLE_DOCS_BOT]: boolean;
  [ConfigCatFeatureFlag.ALLOWED_EXTENSIONS_FOR_SESSION_CONTEXT]: string[];
  [ConfigCatFeatureFlag.ENABLE_MSA]: boolean;
  [ConfigCatFeatureFlag.SHOW_TELEMETRY_IN_SESSIONS]: boolean;
  [ConfigCatFeatureFlag.ENABLE_AUTH0]: boolean;
  [ConfigCatFeatureFlag.ENABLE_UPDATE_PAGERDUTY_SERVICE]: boolean;
  [ConfigCatFeatureFlag.ENABLE_SOURCE_TYPE_SELECTION]: boolean;
  [ConfigCatFeatureFlag.IS_FILE_ATTACHMENT_MANDATORY]: boolean;
  [ConfigCatFeatureFlag.ENABLE_UPDATE_SERVICES]: boolean;
  [ConfigCatFeatureFlag.ENABLE_SESSION_SUMMARY_POLLING]: boolean;
  [ConfigCatFeatureFlag.HIDE_SESSION_STARTER_QUESTIONS]: boolean;
  [ConfigCatFeatureFlag.ENABLE_SPLUNK_TELEPORT]: boolean;
  [ConfigCatFeatureFlag.ENABLE_PROMETHEUS_TELEPORT]: boolean;
  [ConfigCatFeatureFlag.ENABLE_PROJECT_SUMMARY]: boolean;
  [ConfigCatFeatureFlag.OVERRIDE_FAVICON]: string;
  [ConfigCatFeatureFlag.OVERRIDE_LOGO]: string;
  [ConfigCatFeatureFlag.ENABLE_INSTRUCTIONS_BETA]: boolean;
  [ConfigCatFeatureFlag.ENABLE_ADVANCED_INSTRUCTIONS]: boolean;
  [ConfigCatFeatureFlag.SLACK_SIGNUP_LINK]: string;
  [ConfigCatFeatureFlag.ENABLE_PUBLIC_LINK_SHARING]: boolean;
  [ConfigCatFeatureFlag.whitelabeling]: any;
  [ConfigCatFeatureFlag.HIDE_GROUPING_INSTRUCTIONS]: boolean;
  [ConfigCatFeatureFlag.ENABLE_SPLIT_VISUALIZE_GRAPH]: boolean;
}

const initialState: ConfigCatFlagState = {
  [ConfigCatFeatureFlag.CHANGE_API_URL]: false,
  [ConfigCatFeatureFlag.AUTO_GENERATE_AWS_EXTERNAL_ID]: true,
  [ConfigCatFeatureFlag.ENABLED_CONNECTIONS]: [],
  [ConfigCatFeatureFlag.ENABLE_AWS_OIDC_FORM]: false,
  [ConfigCatFeatureFlag.ENABLE_AWS_ACCESS_KEY_FORM]: false,
  [ConfigCatFeatureFlag.ENABLE_SESSION_FILE_UPLOAD]: false,
  [ConfigCatFeatureFlag.ENABLE_DOCS_BOT]: false,
  [ConfigCatFeatureFlag.ALLOWED_EXTENSIONS_FOR_SESSION_CONTEXT]: [
    ".zip",
    ".tgz",
    ".gz",
    ".tar.gz",
    ".txt",
    ".log",
  ],
  [ConfigCatFeatureFlag.ENABLE_MSA]: true,
  [ConfigCatFeatureFlag.SHOW_TELEMETRY_IN_SESSIONS]: true,
  [ConfigCatFeatureFlag.ENABLE_AUTH0]: false,
  [ConfigCatFeatureFlag.ENABLE_UPDATE_PAGERDUTY_SERVICE]: false,
  [ConfigCatFeatureFlag.ENABLE_SOURCE_TYPE_SELECTION]: false,
  [ConfigCatFeatureFlag.IS_FILE_ATTACHMENT_MANDATORY]: false,
  [ConfigCatFeatureFlag.ENABLE_UPDATE_SERVICES]: false,
  [ConfigCatFeatureFlag.ENABLE_SESSION_SUMMARY_POLLING]: false,
  [ConfigCatFeatureFlag.HIDE_SESSION_STARTER_QUESTIONS]: false,
  [ConfigCatFeatureFlag.ENABLE_SPLUNK_TELEPORT]: false,
  [ConfigCatFeatureFlag.ENABLE_PROMETHEUS_TELEPORT]: false,
  [ConfigCatFeatureFlag.ENABLE_PROJECT_SUMMARY]: false,
  [ConfigCatFeatureFlag.OVERRIDE_FAVICON]: "",
  [ConfigCatFeatureFlag.OVERRIDE_LOGO]: "",
  [ConfigCatFeatureFlag.ENABLE_INSTRUCTIONS_BETA]: false,
  [ConfigCatFeatureFlag.ENABLE_ADVANCED_INSTRUCTIONS]: false,
  [ConfigCatFeatureFlag.SLACK_SIGNUP_LINK]: "",
  [ConfigCatFeatureFlag.ENABLE_PUBLIC_LINK_SHARING]: false,
  [ConfigCatFeatureFlag.whitelabeling]: null,
  [ConfigCatFeatureFlag.HIDE_GROUPING_INSTRUCTIONS]: false,
  [ConfigCatFeatureFlag.ENABLE_SPLIT_VISUALIZE_GRAPH]: false,
};

const configCatSlice = createSlice({
  name: "configCat",
  initialState,
  reducers: {
    setFlag: (
      state: ConfigCatFlagState,
      action: PayloadAction<{ name: ConfigCatFeatureFlag; value: any }>
    ) => {
      if (
        action.payload.name === ConfigCatFeatureFlag.OVERRIDE_FAVICON ||
        action.payload.name === ConfigCatFeatureFlag.OVERRIDE_LOGO ||
        action.payload.name === ConfigCatFeatureFlag.SLACK_SIGNUP_LINK
      ) {
        state[action.payload.name] = action.payload.value as string;
      } else {
        state[action.payload.name] = action.payload.value;
      }
    },
    resetToDefaultFlag: (
      state: ConfigCatFlagState,
      action: PayloadAction<ConfigCatFeatureFlag>
    ) => {
      if (
        action.payload === ConfigCatFeatureFlag.OVERRIDE_FAVICON ||
        action.payload === ConfigCatFeatureFlag.OVERRIDE_LOGO ||
        action.payload === ConfigCatFeatureFlag.SLACK_SIGNUP_LINK
      ) {
        state[action.payload] = initialState[action.payload] as string;
      } else {
        state[action.payload] = initialState[action.payload] as any;
      }
    },
    resetAllFlags: (state: ConfigCatFlagState) => {
      Object.assign(state, initialState);
    },
  },
});

export const { setFlag, resetToDefaultFlag, resetAllFlags } = configCatSlice.actions;

export default configCatSlice.reducer;
